import { RecordListInputConfig_Reference } from "@smartsuite/automation-engine-api";
import { actionSolutionId } from "../../../../../utils/action-solutions";
import { actionApplicationId } from "../../../../../utils/record-list-identity";
import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib";
import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { CaptionBlock } from "../../../../../components/CaptionBlock";
import { translateIfPresent } from "../../../../../locales/i18n";
import { useTheme } from "../../../../../stores";
import * as A from "../../../../../stores/automation/mergers/actions";
import { ejectReferences } from "../../../../../stores/automation/utils/filters";
import { useRootStore } from "../../../../../stores/hooks";
import type {
  ActionCandidate,
  ActionGroupType,
} from "../../../../../types/candidate";
import {
  recordListInputConfigToOptionValue,
  recordsSourceOptionToRecordListInputConfig,
} from "../../../../../utils/conditions-coverter.hook";
import {
  ActionLabel,
  INPUT_APPLICATION_ID,
} from "../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import {
  findInputAndPick,
  pickValueConfigString,
} from "../../../../../utils/inputs";
import { RecordsNoOptions } from "./components/RecordsNoOptions/RecordsNoOptions";
import { RecordsOption } from "./components/RecordsOption/RecordsOption";
import { RecordsSelectedOption } from "./components/RecordsSelectedOption/RecordsSelectedOption";
import * as S from "./style";
import type { RecordsSourceOption } from "./utils";
import {
  findRecordsActionsToOptions,
  getAddNewOption,
  getEmptyOption,
  triggerToOption,
} from "./utils";
import { referenceMatches } from "../utils";
import { getSourceActionReference } from "../FieldEditor/FieldEditorRow/fields/utils";
import { locateEnclosingLoop } from "../../../../../stores/automation/utils/action-group-walk";
import { observer } from "mobx-react-lite";
import { t } from "i18next";

export const RecordsSourceEditor: React.FC<{
  action: ActionCandidate;
  onChange: (action: ActionCandidate) => void;
}> = observer(({ action, onChange }) => {
  const store = useRootStore();
  const { candidate } = store.automations;
  const looping = store.automations.candidate.looping;
  // The ENCLOSING loop, climbing past branch containers (locateActionByHash
  // would stop at the branch): an action inside a branch that sits inside a
  // loop still belongs to that loop's Current-Item scope.
  const loopInstanceId = locateEnclosingLoop(
    candidate.self.actionGroups,
    action.hash,
  );
  const actionGroupType: ActionGroupType =
    loopInstanceId !== undefined ? "loop" : "actions";
  const integrations = store.integrations.list;

  const [options, setOptions] = useState<RecordsSourceOption[]>([]);
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string | undefined>(() =>
    recordListInputConfigToOptionValue(action.recordList),
  );
  const [showNoOptions, setShowNoOptions] = useState<boolean>(false);
  const [shouldSetDefault, setShouldSetDefault] = useState<boolean>(true);

  const loopingSourceActionReference = getSourceActionReference(
    loopInstanceId !== undefined ? looping.sourceAt(loopInstanceId) : undefined,
  );

  useEffect(() => {
    candidate.initDeletedActions(action, store.config.solutionId);

    if (action.description.recordListInput !== undefined) {
      const findRecordsActions = candidate.findActionsWithRecordsList(
        action,
        actionGroupType,
      );

      const actionOptions = findRecordsActionsToOptions(findRecordsActions);
      const triggerOption = triggerToOption(candidate.self.trigger, action);

      if (actionOptions.length === 0 && triggerOption === undefined) {
        setValue(undefined);
        setShowNoOptions(true);
        ejectReferences(action, "actionReference");
        action.recordList = {
          recordList: {
            oneofKind: "reference",
            reference: RecordListInputConfig_Reference.NO_RECORD_LIST,
          },
          metadata: {},
        };
        onChange(action);
        return;
      }

      setDefault(recordListInputConfigToOptionValue(action.recordList));

      setShowNoOptions(false);
      if (
        (value === "trigger" || value === undefined) &&
        triggerOption === undefined
      ) {
        setValue(actionOptions[0].value);
        action.recordList = recordsSourceOptionToRecordListInputConfig(
          actionOptions[0],
        );
        onChange(action);
      } else if (value === undefined && triggerOption === undefined) {
        setValue(actionOptions[0].value);
        action.recordList = recordsSourceOptionToRecordListInputConfig(
          actionOptions[0],
        );
        onChange(action);
      } else if (value === undefined && triggerOption !== undefined) {
        setValue("trigger");
        action.recordList =
          recordsSourceOptionToRecordListInputConfig(triggerOption);
        onChange(action);
      } else if (value !== undefined && value !== "trigger") {
        const optionAvailable = actionOptions.find(
          (actionOption) => actionOption.value === value,
        );
        if (optionAvailable === undefined) {
          if (triggerOption !== undefined) {
            setValue("trigger");
            action.recordList =
              recordsSourceOptionToRecordListInputConfig(triggerOption);
            onChange(action);
          } else {
            setValue(actionOptions[0].value);
            action.recordList = recordsSourceOptionToRecordListInputConfig(
              actionOptions[0],
            );
            onChange(action);
          }
        }
      }
      if (actionOptions.length === 0) {
        actionOptions.push(getEmptyOption());
      }
      if (triggerOption !== undefined) {
        actionOptions.unshift(triggerOption);
      }
      actionOptions.push(getAddNewOption());
      setOptions(actionOptions);
    }
  }, [action]);

  const setDefault = (value: string | undefined): void => {
    if (shouldSetDefault) {
      setValue(value);
    }
    setShouldSetDefault(false);
  };

  const onOpenedChange = (e: boolean): void => {
    setOpen(e);
  };

  if (action.description.recordListInput === undefined) {
    return null;
  }

  const createNewFindAction = (): void => {
    const newFindActionHash = uuid();
    // The new action has to search somewhere: the table this action already
    // works on, else the one the trigger watches.
    const applicationId =
      actionApplicationId(action) ??
      findInputAndPick(
        candidate.self.trigger.inputs,
        INPUT_APPLICATION_ID,
        pickValueConfigString,
      );
    const solutionId = actionSolutionId(action, store.config.solutionId);
    if (applicationId === undefined) {
      return;
    }

    // Find Records is always a SmartSuite action, whichever integration owns
    // the action taking its records.
    const integrationId =
      integrations.find((i) => i.integrationId.startsWith("ssi"))
        ?.integrationId ?? "ssi";

    // New Find Records actions are always created in the ROOT actions
    // group -- root-level actions are legal upstream sources for any
    // action, regardless of where the current action lives (branch/loop).
    candidate.setActions(
      [
        A.mergeNewActionWithApplicationSolution(
          newFindActionHash,
          integrationId,
          ActionLabel.findRecords,
          applicationId,
          solutionId,
        ),
      ],
      "actions",
    );

    const actions = candidate.getGroupActions("actions");
    const moveBeforeActionIndex = actions.findIndex(
      (a) => a.hash === action.hash,
    );

    // moveBeforeActionIndex is -1 when the current action isn't itself in
    // the root list (it lives in a branch/loop) -- appending at the end is
    // fine in that case, no move needed.
    if (moveBeforeActionIndex >= 0) {
      candidate.setActions(
        [A.moveAction(actions.length - 1, moveBeforeActionIndex)],
        "actions",
      );
    }

    candidate.setChangeActiveActionTo(newFindActionHash);
  };

  if (showNoOptions) {
    return <RecordsNoOptions createNewFindAction={createNewFindAction} />;
  }
  const { label, description } = action.description.recordListInput;

  const getTypeDescription = (
    option: RecordsSourceOption,
  ): string | undefined => {
    const isLoopingSource =
      actionGroupType === "loop" &&
      loopingSourceActionReference &&
      option?.actionReference &&
      referenceMatches(loopingSourceActionReference)(option.actionReference);

    return isLoopingSource ? String(t("looping.current-item-in")) : undefined;
  };

  const renderOption = (
    option: RecordsSourceOption,
    active: boolean,
  ): React.ReactNode => {
    const typeDescription = getTypeDescription(option);

    return (
      <RecordsOption
        topLabel={typeDescription}
        option={option}
        active={active}
      />
    );
  };

  const renderSelectedOption = (
    option: RecordsSourceOption,
  ): React.ReactNode => {
    const typeDescription = getTypeDescription(option);
    return <RecordsSelectedOption option={option} topLabel={typeDescription} />;
  };

  return (
    <S.RecordsSourceEditorWrapper>
      <S.GlobalStyle />
      <CaptionBlock
        description={{
          label: translateIfPresent(`caption.${label}`, label),
          subLabel: translateIfPresent(`caption.${description}`, description),
          icon: "",
          dynamic: false,
        }}
      />
      <KeyValueCustomSelect
        my="left top+2"
        state={open ? "active" : "default"}
        showClear={false}
        placeholderArrowStyle="filled-dark"
        className={
          open
            ? "container-is-open records-source-selector"
            : "records-source-selector"
        }
        portalStyles={getLocalCssColorVariables(theme)}
        options={options}
        onOpenedChange={onOpenedChange}
        onChange={(value) => {
          switch (value) {
            case "no-actions": {
              return;
            }
            case "add-new-find": {
              // create new find action
              createNewFindAction();
              return;
            }
          }
          setValue(String(value));
          const option = options.find((option) => option.value === value);
          if (option === undefined) {
            return;
          }
          action.recordList =
            recordsSourceOptionToRecordListInputConfig(option);
          // No explicit groupType here: setActions already routes by
          // currentGroupLocator (set when the action's panel opened via
          // setActionGroupByActionHash) -- passing the derived "actions"
          // for a branch/nested-loop action would bypass that routing and
          // silently write into the ROOT actions group instead.
          candidate.setActions([A.mergeActionWithNoActionReferences(action)]);

          onChange(action);
        }}
        renderOption={renderOption}
        renderSelectedOption={renderSelectedOption}
        value={value}
        listItemsClassname="records-source-selector-list-items"
      />
    </S.RecordsSourceEditorWrapper>
  );
});
